import React, {ReactNode, useCallback, useMemo} from "react";
import "./PointItem.css";
import {useFormikContext} from "formik";
import {WalkMap, WalkMapPoint} from "./types";
import {getCumulativeDistancesKM} from "@walkways/shared";
import {Button, ButtonGroup} from "react-bootstrap";
// https://react-icons.github.io/react-icons/icons/fa/
import {
  FaChevronDown,
  FaChevronUp,
  FaPencilAlt,
  FaTrashAlt,
} from "react-icons/fa";
import PointEdit from "./PointEdit";
import {useEditContext} from "./EditContext";

type PointItemProps = {
  point: WalkMapPoint;
  index: number;
};

const PointItem = ({point, index}: PointItemProps) => {
  const {values: walkMap, setFieldValue} = useFormikContext<WalkMap>();
  const {editNode, setEditNode} = useEditContext();

  const swap = useCallback(
    (swapIndex: number) => {
      const moved = walkMap.points[index];
      walkMap.points[index] = walkMap.points[swapIndex];
      walkMap.points[swapIndex] = moved;
      setFieldValue("points", [...walkMap.points]);
    },
    [walkMap.points, setFieldValue, index],
  );
  const moveUp = useCallback(() => {
    swap(index - 1);
  }, [swap, index]);
  const moveDown = useCallback(() => {
    swap(index + 1);
  }, [swap, index]);
  const remove = useCallback(() => {
    walkMap.points.splice(index, 1);
    setFieldValue("points", [...walkMap.points]);
  }, [walkMap.points, setFieldValue, index]);
  const edit = useCallback(() => {
    setEditNode(<PointEdit index={index} />);
  }, [index, setEditNode]);

  const distancePX = useMemo<number>(() => {
    if (index === 0) {
      return 0;
    }

    const point1 = walkMap.points[index - 1];
    const point2 = walkMap.points[index];

    return Math.sqrt(
      Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2),
    );
  }, [index, walkMap.points]);
  const distanceKM = useMemo<number>(() => {
    if (index === 0) {
      return 0;
    }

    const cumulativeDistancesKM = getCumulativeDistancesKM(walkMap);
    return cumulativeDistancesKM[index] - cumulativeDistancesKM[index - 1];
  }, [index, walkMap]);

  return (
    <li className="PointItem">
      <div className="PointItem-label">
        <span className="PointItem-name">
          {point.name || `Point ${index + 1}`}
        </span>
        <div className="PointItem-coordinate">
          <span>{`(${point.x}, ${point.y})`}</span>
          {!!distancePX && (
            <>
              <span>{Math.round(distancePX)}px from prev</span>
              <span>{Math.round(distanceKM)}km from prev</span>
            </>
          )}
        </div>
      </div>
      <ButtonGroup className="PointItem-buttons">
        <Button
          variant="outline-dark"
          onClick={moveDown}
          disabled={!!editNode || index === walkMap.points.length - 1}>
          <FaChevronDown />
        </Button>
        <Button
          variant="outline-dark"
          onClick={moveUp}
          disabled={!!editNode || index === 0}>
          <FaChevronUp />
        </Button>
        <Button variant="outline-dark" onClick={edit}>
          <FaPencilAlt />
        </Button>
        <Button variant="outline-dark" onClick={remove}>
          <FaTrashAlt />
        </Button>
      </ButtonGroup>
    </li>
  );
};

export default PointItem;
